import { observer } from 'mobx-react-lite';
import { Link } from 'react-router-dom';
import LanguageStore from '../../store/LanguageStore';
import { formatNumber } from '../../utils/formatNumber';
import './videos-table.scss';

const VideosTable = observer(({ 
    videos, 
    pagination, 
    sortBy, 
    sortOrder, 
    onSort, 
    onPageChange,
    loading 
}) => {
    const t = LanguageStore.t;
    const { language } = LanguageStore;

    const getSortIcon = (field) => {
        if (sortBy !== field) {
            return '⇅'; // Нейтральная иконка
        }
        return sortOrder === 'ASC' ? '↑' : '↓';
    };

    const handleSortClick = (field) => {
        onSort(field);
    };

    const renderPagination = () => {
        const pages = [];
        const totalPages = pagination.totalPages;
        const currentPage = pagination.page;

        // Показываем максимум 7 страниц
        let startPage = Math.max(1, currentPage - 3);
        let endPage = Math.min(totalPages, currentPage + 3);

        if (startPage > 1) {
            pages.push(
                <button
                    key={1}
                    onClick={() => onPageChange(1)}
                    className="videos-table__page-btn"
                >
                    1
                </button>
            );
            if (startPage > 2) {
                pages.push(<span key="ellipsis-start" className="videos-table__ellipsis">...</span>);
            }
        }

        for (let i = startPage; i <= endPage; i++) {
            pages.push(
                <button
                    key={i}
                    onClick={() => onPageChange(i)}
                    className={`videos-table__page-btn ${i === currentPage ? 'videos-table__page-btn--active' : ''}`}
                >
                    {i}
                </button>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                pages.push(<span key="ellipsis-end" className="videos-table__ellipsis">...</span>);
            }
            pages.push(
                <button
                    key={totalPages}
                    onClick={() => onPageChange(totalPages)}
                    className="videos-table__page-btn"
                >
                    {totalPages}
                </button>
            );
        }

        return pages;
    };

    return (
        <div className="videos-table">
            <div className="videos-table__wrapper">
                <table className="videos-table__table">
                    <thead>
                        <tr>
                            <th 
                                className="videos-table__header videos-table__header--sortable"
                                onClick={() => handleSortClick('title')}
                            >
                                {language === 'ru' ? 'Название' : 'Title'}
                                <span className="videos-table__sort-icon">
                                    {getSortIcon('title')}
                                </span>
                            </th>
                            <th 
                                className="videos-table__header videos-table__header--sortable"
                                onClick={() => handleSortClick('viewCount')}
                            >
                                {t('views') || 'Views'}
                                <span className="videos-table__sort-icon">
                                    {getSortIcon('viewCount')}
                                </span>
                            </th>
                            <th 
                                className="videos-table__header videos-table__header--sortable"
                                onClick={() => handleSortClick('likeCount')}
                            >
                                {t('likes') || 'Likes'}
                                <span className="videos-table__sort-icon">
                                    {getSortIcon('likeCount')}
                                </span>
                            </th>
                            <th 
                                className="videos-table__header videos-table__header--sortable"
                                onClick={() => handleSortClick('commentCount')}
                            >
                                {t('comments') || 'Comments'}
                                <span className="videos-table__sort-icon">
                                    {getSortIcon('commentCount')}
                                </span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && videos.length === 0 ? (
                            <tr>
                                <td colSpan="4" className="videos-table__loading">
                                    {t('loading') || 'Loading...'}
                                </td>
                            </tr>
                        ) : videos.length === 0 ? (
                            <tr>
                                <td colSpan="4" className="videos-table__empty">
                                    {language === 'ru' ? 'Видео не найдены' : 'No videos found'}
                                </td>
                            </tr>
                        ) : (
                            videos.map((video) => (
                                <tr key={video.videoId} className="videos-table__row">
                                    <td className="videos-table__cell videos-table__cell--title">
                                        <div className="videos-table__video">
                                            {video.imageWebUrl && (
                                                <img
                                                    src={video.imageWebUrl}
                                                    alt={video.title}
                                                    className="videos-table__video-image"
                                                    width="80"
                                                    height="45"
                                                />
                                            )}
                                            <Link
                                                to={`/video/${video.videoId}`}
                                                className="videos-table__video-link"
                                            >
                                                {video.title}
                                            </Link>
                                        </div>
                                    </td>
                                    <td className="videos-table__cell">
                                        {formatNumber(video.viewCount)}
                                    </td>
                                    <td className="videos-table__cell">
                                        {formatNumber(video.likeCount)}
                                    </td>
                                    <td className="videos-table__cell">
                                        {formatNumber(video.commentCount)}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {pagination.totalPages > 1 && (
                <div className="videos-table__pagination">
                    <button
                        onClick={() => onPageChange(pagination.page - 1)}
                        disabled={pagination.page === 1 || loading}
                        className="videos-table__page-btn videos-table__page-btn--nav"
                    >
                        {language === 'ru' ? '← Назад' : '← Previous'}
                    </button>
                    
                    <div className="videos-table__page-numbers">
                        {renderPagination()}
                    </div>

                    <button
                        onClick={() => onPageChange(pagination.page + 1)}
                        disabled={pagination.page === pagination.totalPages || loading}
                        className="videos-table__page-btn videos-table__page-btn--nav"
                    >
                        {language === 'ru' ? 'Вперед →' : 'Next →'}
                    </button>
                </div>
            )}

            {pagination.totalItems > 0 && (
                <div className="videos-table__info">
                    {language === 'ru' 
                        ? `Показано ${(pagination.page - 1) * pagination.limit + 1}-${Math.min(pagination.page * pagination.limit, pagination.totalItems)} из ${pagination.totalItems}`
                        : `Showing ${(pagination.page - 1) * pagination.limit + 1}-${Math.min(pagination.page * pagination.limit, pagination.totalItems)} of ${pagination.totalItems}`
                    }
                </div>
            )}
        </div>
    );
});

export default VideosTable;

